<template>
  <v-card :flat="simple">
    <v-card-title class="px-6">
      {{ title }}
      <template v-if="simple">
        <v-spacer></v-spacer>
        <v-btn icon @click="$emit('close')">
          <v-icon>close</v-icon>
        </v-btn>
      </template>
    </v-card-title>
    <v-divider></v-divider>
    <v-card-text class="subtitle-1 px-6 pt-6">
      <v-form ref="form" v-model="valid">
        <v-row dense>
          <v-col cols="12" class="font-weight-bold">
            When would you like to start culture tracking?
          </v-col>
          <v-col cols="12">
            Set a date for your team to receive their survey
          </v-col>
          <v-col cols="auto">
            <v-menu
              v-model="startdate_menu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="formattedStartDate"
                  outlined
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="startDate = null"
                  :rules="[$rules.required]"
                  append-icon="today"
                  @click:append="startdate_menu = true"
                  @change="changeStartDate"
                >
                  <template v-slot:append-outer>
                    <v-btn
                      v-if="scheduled_pulse"
                      color="error"
                      text
                      @click="pause"
                      class="font-weight-bold"
                    >
                      Cancel schedule
                    </v-btn>
                  </template>
                </v-text-field>
              </template>
              <v-date-picker
                v-model="startDate"
                @input="startdate_menu = false"
                @change="changeStartDate"
                :min="min"
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
        <v-row dense v-if="recurringRules.length">
          <v-col cols="12" class="font-weight-bold">
            Tracking Frequency
          </v-col>
          <v-col cols="12">
            How often would you like us to send culture tracking?
          </v-col>
          <v-col cols="auto">
            <v-select
              v-model="settings.pulseRecurringRule"
              :items="recurringRules"
              label="Frequency"
              outlined
              :rules="[$rules.required]"
              required
              @change="updateFrequency"
            >
            </v-select>
          </v-col>
        </v-row>
        <v-row
          dense
          v-if="
            frequencyId != this.$enums.frequency.QUARTERLY &&
              frequencyId != this.$enums.frequency.ANNUAL
          "
        >
          <v-col cols="12" class="font-weight-bold">
            Questions Per Send
          </v-col>
          <v-col cols="12">
            How many questions would you like each member to answer per survey?
          </v-col>
          <v-col cols="auto">
            <v-select
              outlined
              v-model="settings.pulseNumberOfQuestionsPerPeriod"
              :items="number_of_questions"
              item-text="name"
              item-value="id"
              :rules="[$rules.required]"
              required
              class="min-width"
            ></v-select>
          </v-col>
        </v-row>
        <template v-if="!simple">
          <v-row dense>
            <v-col cols="12" class="font-weight-bold">
              Set anonymity minimum [Protect users identity]
            </v-col>
            <v-col cols="12">
              We won’t show data results for segments less than
              {{ settings.pulseEmployeeLimit }} people.
            </v-col>
            <v-col cols="auto">
              <v-select
                outlined
                v-model="settings.pulseEmployeeLimit"
                :items="employee_limits"
                item-text="name"
                item-value="id"
                :rules="[$rules.required]"
                required
                class="min-width"
              ></v-select>
            </v-col>
          </v-row>
          <v-row dense class="pb-8">
            <v-col cols="12" class="font-weight-bold">
              Tracking Questions
            </v-col>
            <v-col cols="12">
              What should we ask your employees?
            </v-col>
            <v-col cols="auto">
              <v-btn
                color="secondary"
                outlined
                to="/features/tracking/settings/questions"
                class="mr-4"
              >
                Manage Questions
              </v-btn>
              {{ overview.enabledQuestionCount }} questions enabled
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12">
              <info-card
                icon="$vuetify.icons.pulse"
                title="Culture Tracking Summary"
              >
                CoreCounts will survey your employees
                {{ frequency_label }} using the questions you've set in our
                <router-link
                  class="info--text"
                  to="/features/tracking/settings/questions"
                  >question management</router-link
                >
                area. Our question algorithm will ensure you get max coverage
                <template
                  v-if="
                    frequencyId != this.$enums.frequency.QUARTERLY &&
                      frequencyId != this.$enums.frequency.ANNUAL
                  "
                  >while honoring the
                  {{ settings.pulseNumberOfQuestionsPerPeriod }} question limit
                  you've set above</template
                >. All data that is collected can be viewed in your
                <router-link class="info--text" to="/reporting/culture"
                  >culture tracking report</router-link
                >.
              </info-card>
            </v-col>
          </v-row>
        </template>
      </v-form>
    </v-card-text>
    <v-card-actions class="pt-0 px-6">
      <v-btn color="primary" large :loading="saving" @click="save" class="mr-4">
        <v-icon class="mr-2">
          $vuetify.icons.pulse
        </v-icon>
        Save
      </v-btn>
      <v-btn
        color="accent"
        text
        to="/features/tracking/settings/questions"
        class="mr-4"
        v-if="simple"
      >
        Manage Questions >
      </v-btn>
      <template v-else>
        <v-icon color="lighttext" class="mr-2">person_outline</v-icon>
        <span class="lighttext--text">
          All culture tracking survey data is anonymous and employee's can't be
          identified
        </span>
      </template>
    </v-card-actions>
    <v-card-text v-if="simple">
      <v-icon color="lighttext" class="mr-2">person_outline</v-icon>
      <span class="lighttext--text">
        All culture tracking survey data is anonymous and employee's can't be
        identified
      </span>
    </v-card-text>
    <pulse-schedule-success-dialog
      ref="successDialog"
      v-on:close="$emit('save')"
      :scheduled-date="startDate"
    ></pulse-schedule-success-dialog>
  </v-card>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";
import { rrulestr } from "rrule";
import PulseScheduleSuccessDialog from "@/components/pulse/PulseScheduleSuccessDialog";
export default {
  name: "PulseSchedule",
  components: { PulseScheduleSuccessDialog },
  props: {
    simple: { type: Boolean, default: false },
    title: { type: String, default: "Culture Tracking Schedule" }
  },
  data: () => ({
    dialog: false,
    saving: false,
    valid: false,
    startdate_menu: false,
    startDate: null,
    employee_limits: [3, 4, 5],
    benchmark_sends: [1, 2, 3, 4, 5, 6],
    yesNoItems: [
      { id: true, name: "Yes" },
      { id: false, name: "No" }
    ]
  }),
  watch: {
    pulses(val, oldVal) {
      if (val != oldVal) {
        this.load();
      }
    }
  },
  computed: {
    filtered_benchmark_sends() {
      return this.benchmark_sends.filter(x => x > this.pulse_sent);
    },
    min() {
      return moment()
        .add(1, "days")
        .format("YYYY-MM-DD");
    },
    frequency_label() {
      switch (this.settings.pulseFrequencyId) {
        case this.$enums.frequency.WEEKLY:
          return "weekly";
        case this.$enums.frequency.BI_WEEKLY:
          return "bi-weekly";
        case this.$enums.frequency.MONTHLY:
          return "monthly";
        case this.$enums.frequency.QUARTERLY:
          return "quarterly";
        case this.$enums.frequency.ANNUAL:
          return "annually";
        default:
          return "";
      }
    },
    frequencyId() {
      return (
        this.recurringRules.find(
          x => x.value == this.settings.pulseRecurringRule
        ) || {}
      ).frequencyId;
    },
    formattedStartDate() {
      if (this.startDate) {
        return moment(this.startDate).format("dddd, MMMM Do, YYYY");
      }
      return "";
    },
    number_of_questions() {
      switch (this.settings.pulseFrequencyId) {
        case this.$enums.frequency.WEEKLY:
          return [3, 4, 5, 6, 7, 8, 9, 10];
        case this.$enums.frequency.BI_WEEKLY:
          return [10, 11, 12, 13, 14, 15];
        case this.$enums.frequency.MONTHLY:
          return [15, 16, 17, 18, 19, 20];
        default:
          return [];
      }
    },
    default_number_of_questions() {
      switch (this.settings.pulseFrequencyId) {
        case this.$enums.frequency.WEEKLY:
          return 5;
        case this.$enums.frequency.BI_WEEKLY:
          return 10;
        case this.$enums.frequency.MONTHLY:
          return 20;
        default:
          return 0;
      }
    },
    scheduled_pulse() {
      return this.pulses.find(x => x.sentDate == null);
    },
    recurringRules() {
      if (!this.startDate) {
        return [];
      }
      let startDate = moment(this.startDate);
      let firstDayOfWeekOfMonth = moment(this.startDate)
        .startOf("month")
        .day(startDate.isoWeekday());
      if (firstDayOfWeekOfMonth.date() > 7) firstDayOfWeekOfMonth.add(7, "d");
      let dayIndex = 1;
      while (dayIndex < 5) {
        if (firstDayOfWeekOfMonth.isSame(startDate)) {
          break;
        }
        firstDayOfWeekOfMonth.add(7, "d");
        dayIndex++;
      }

      let items = [];
      let rule = rrulestr(
        `FREQ=WEEKLY;BYDAY=${startDate.format("dd").toUpperCase()}`
      );
      items.push({
        value: rule.toString(),
        text: rule.toText(),
        frequencyId: this.$enums.frequency.WEEKLY
      });
      rule = rrulestr(
        `FREQ=WEEKLY;INTERVAL=2;BYDAY=${startDate.format("dd").toUpperCase()}`
      );
      items.push({
        value: rule.toString(),
        text: rule.toText(),
        frequencyId: this.$enums.frequency.BI_WEEKLY
      });
      rule = rrulestr(`FREQ=MONTHLY;BYMONTHDAY=${startDate.date()}`);
      items.push({
        value: rule.toString(),
        text: rule.toText(),
        frequencyId: this.$enums.frequency.MONTHLY
      });
      if (dayIndex < 5) {
        rule = rrulestr(
          `FREQ=MONTHLY;BYDAY=+${dayIndex}${startDate
            .format("dd")
            .toUpperCase()};BYSETPOS=1`
        );
        items.push({
          value: rule.toString(),
          text: rule.toText(),
          frequencyId: this.$enums.frequency.MONTHLY
        });
      }
      rule = rrulestr(`FREQ=MONTHLY;INTERVAL=3;BYMONTHDAY=${startDate.date()}`);
      items.push({
        value: rule.toString(),
        text: rule.toText(),
        frequencyId: this.$enums.frequency.QUARTERLY
      });
      if (dayIndex < 5) {
        rule = rrulestr(
          `FREQ=MONTHLY;INTERVAL=3;BYDAY=+${dayIndex}${startDate
            .format("dd")
            .toUpperCase()};BYSETPOS=1`
        );
        items.push({
          value: rule.toString(),
          text: rule.toText(),
          frequencyId: this.$enums.frequency.QUARTERLY
        });
      }
      rule = rrulestr(`FREQ=YEARLY;INTERVAL=1;BYMONTHDAY=${startDate.date()}`);
      items.push({
        value: rule.toString(),
        text: rule.toText(),
        frequencyId: this.$enums.frequency.ANNUAL
      });
      return items;
    },
    ...mapGetters({
      overview: "pulse/overview",
      settings: "profile/settings",
      pulses: "pulse/pulses",
      pulse_sent: "pulse/pulse_sent"
    })
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      if (this.simple) {
        this.startDate = moment()
          .add(1, "days")
          .format("YYYY-MM-DD");
      } else {
        this.startDate = this.scheduled_pulse
          ? moment(this.scheduled_pulse.scheduleDate).format("YYYY-MM-DD")
          : null;
      }
    },
    updateFrequency() {
      this.settings.pulseFrequencyId = this.frequencyId;
      if (
        this.number_of_questions.indexOf(
          this.settings.pulseNumberOfQuestionsPerPeriod
        ) < 0
      ) {
        this.settings.pulseNumberOfQuestionsPerPeriod = this.default_number_of_questions;
      }
    },
    changeStartDate() {
      if (
        !this.recurringRules.find(
          x => x.value == this.settings.pulseRecurringRule
        )
      ) {
        this.settings.pulseRecurringRule = "";
      }
    },
    cancel() {
      this.$emit("cancel");
    },
    save() {
      if (this.$refs.form.validate()) {
        let pulseAlreadyScheduled =
          this.pulses.filter(x => x.sentDate == null && !x.isRegistration)
            .length > 0;
        this.saving = true;
        this.$store
          .dispatch("settings/savePulseSettings", this.settings)
          .then(() => {
            let start = {
              startDate: this.startDate,
              startTime: "09:00",
              startTimezone: moment.tz.guess(true)
            };
            this.$store
              .dispatch("pulse/start", start)
              .then(() => {
                if (pulseAlreadyScheduled) {
                  this.$root.$snackbarSuccess("Culture tracking updated");
                  this.$emit("save");
                } else {
                  this.$emit("success");
                  this.$refs.successDialog.showDialog();
                }
              })
              .catch(error => {
                this.logError(error);
                this.$root.$alert(
                  "Error Occurred",
                  "An error occurred while saving your culture tracking settings."
                );
              });
          })
          .catch(error => {
            this.logError(error);
            this.$root.$alert(
              "Error Occurred",
              "An error occurred while saving your culture tracking settings."
            );
          })
          .finally(() => {
            this.saving = false;
          });
      }
    },
    pause() {
      this.$store
        .dispatch("pulse/pause")
        .then(() => {
          this.$root.$snackbarSuccess("Pulse paused");
          this.$emit("pause");
        })
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occurred while pausing culture tracking."
          );
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.v-text-field--outlined ::v-deep .v-input__append-outer {
  margin-top: 10px !important;
}
</style>
